import {
  Accordion,
  Button,
  Chip,
  Group,
  LoadingOverlay,
  Modal,
  useComputedColorScheme,
  Text,
  Radio,
  Stack,
  Alert,
  Divider,
  List,
  Anchor,
  Checkbox,
} from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker } from '@mantine/dates';
import { useValidateSubmission, prefetchClaimById } from 'api/resource';
import { useQueryClient } from '@tanstack/react-query';
import { SubmitSplitButton } from './SubmitSplitButton';
import { Claim, Payor, PayorType, Provider, SubmissionType } from 'interfaces';
import { ProviderSelect } from 'components/ProviderSelect';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useClaimEditStepperStore } from 'store/ClaimEditStepperStore';
import { buttonTextColor } from 'utils/buttonUtils';
import {
  hasCompleteSelectedDateRange,
  hasPartialSelectedDateRange,
  type SubmissionDateRange,
} from 'utils/submissionCriteria';
import { ClaimBatchReviewModal } from './ClaimBatchReviewModal';
import { MRT_TableInstance } from 'mantine-react-table';

import * as Sentry from '@sentry/react';

interface SubmissionModalProps {
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
  handleOpenModal: () => void;
  providers: Provider[];
  payors: Payor[];
  table: MRT_TableInstance<Claim>;
  submissionType: SubmissionType;
  setSubmissionType: (value: SubmissionType) => void;
  payorType: string | null;
  setPayorType: (value: string | null) => void;
  handleSubmission: (autoSubmit: boolean) => Promise<void>;
  disableSubmitButton: () => boolean;
  selectedDateRange: SubmissionDateRange;
  setSelectedDateRange: (value: SubmissionDateRange) => void;
  selectedProvider: string;
  setSelectedProvider: (value: string) => void;
}

export const SubmissionModal = ({
  openModal,
  setOpenModal,
  handleOpenModal,
  providers,
  payors,
  table,
  submissionType,
  setSubmissionType,
  payorType,
  setPayorType,
  handleSubmission,
  disableSubmitButton,
  selectedDateRange,
  setSelectedDateRange,
  selectedProvider,
  setSelectedProvider,
}: SubmissionModalProps) => {
  const { id, apiID, province } = useCurrentSiteStore();
  const setStepperQueue = useClaimEditStepperStore((state) => state.setQueue);
  const queryClient = useQueryClient();

  const computedColorScheme = useComputedColorScheme('dark');
  const [isNavigating, setIsNavigating] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [submitNonErrors, setSubmitNonErrors] = useState(false);
  const prevReviewModalOpen = useRef(reviewModalOpen);
  const publicPayors = useMemo(
    () =>
      payors.filter(
        (payor) =>
          payor.type === PayorType.PUBLIC &&
          (payor.province === province || payor.province === null)
      ),
    [payors, province]
  );

  const {
    data: claimData,
    isLoading,
    refetch: refetchClaimCount,
  } = useValidateSubmission({
    siteID: id,
    type: submissionType,
    startDate: selectedDateRange[0] || '',
    endDate: selectedDateRange[1] || '',
    providerID: selectedProvider || '',
    payorType: payorType || '',
  });
  const hasCompleteDateRange = hasCompleteSelectedDateRange(selectedDateRange);
  const hasPartialDateRange = hasPartialSelectedDateRange(selectedDateRange);
  const canValidateSubmission = (() => {
    if (!openModal || !submissionType || !payorType) return false;

    switch (submissionType) {
      case SubmissionType.ready:
        return true;
      case SubmissionType.date:
        return hasCompleteDateRange;
      case SubmissionType.provider:
        return !!selectedProvider && !hasPartialDateRange;
    }
  })();

  useEffect(() => {
    if (canValidateSubmission) {
      refetchClaimCount();
    }
  }, [canValidateSubmission, refetchClaimCount]);

  useEffect(() => {
    if (payorType && !publicPayors.some((payor) => payor.name === payorType)) {
      setPayorType(null);
    }
  }, [payorType, publicPayors, setPayorType]);

  useEffect(() => {
    const closedReview =
      prevReviewModalOpen.current && !reviewModalOpen && openModal;
    if (closedReview && canValidateSubmission) {
      refetchClaimCount();
    }
    prevReviewModalOpen.current = reviewModalOpen;
  }, [canValidateSubmission, reviewModalOpen, openModal, refetchClaimCount]);
  const validationErrors =
    claimData &&
    typeof claimData === 'object' &&
    Array.isArray(claimData.errors)
      ? claimData.errors.filter(Boolean)
      : [];
  const claimCount =
    typeof claimData === 'number'
      ? claimData
      : claimData?.claimsCount ?? claimData?.claimCount ?? 0;

  const closeModal = () => {
    setSubmissionType(null);
    setPayorType('');
    setSelectedDateRange([null, null]);
    setSelectedProvider('');
    setOpenModal(false);
  };

  const handleSubmissionTypeChange = (value: SubmissionType) => {
    setSubmissionType(value);
    if (value !== SubmissionType.date && value !== SubmissionType.provider) {
      setSelectedDateRange([null, null]);
    }
    if (value !== SubmissionType.provider) {
      setSelectedProvider('');
    }
  };

  const parseValidationError = (error: string) => {
    const match = error.match(
      /^\(([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})\)/
    );
    const claimID = match?.[1] ?? null;
    const messagePortion = match
      ? error.slice(match.index! + match[0].length).trim()
      : error;
    const messages = messagePortion
      .split(';')
      .map((msg) => msg.trim())
      .filter(Boolean);

    return { claimID, messages: messages.length ? messages : [error] };
  };

  const { groupedClaimErrors, generalErrors } = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    const general: string[] = [];

    validationErrors.forEach((error) => {
      const { claimID, messages } = parseValidationError(error);
      if (!claimID) {
        general.push(...messages);
        return;
      }
      grouped[claimID] = [...(grouped[claimID] || []), ...messages];
    });

    return { groupedClaimErrors: grouped, generalErrors: general };
  }, [validationErrors]);

  const erroredClaimIDs = useMemo(
    () => Object.keys(groupedClaimErrors).filter(Boolean),
    [groupedClaimErrors]
  );

  const handleReviewErroredClaims = async () => {
    if (!erroredClaimIDs.length) return;
    setIsNavigating(true);
    try {
      const firstClaimId = erroredClaimIDs[0];
      setStepperQueue(erroredClaimIDs);
      await prefetchClaimById(queryClient, firstClaimId);
      setReviewModalOpen(true);
    } catch (error) {
      Sentry.captureException(error);
      setReviewModalOpen(true);
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <Modal
      title="Create Submission File"
      opened={openModal}
      onClose={closeModal}
      size="lg"
      data-testid="submission-modal"
    >
      <Stack
        align="stretch"
        justify="center"
        mx="lg"
        gap="md"
        style={{ position: 'relative' }}
      >
        <LoadingOverlay visible={isNavigating} overlayProps={{ blur: 2 }} />
        <Radio.Group
          label="Submission Type"
          value={submissionType ?? null}
          onChange={(value) =>
            value && handleSubmissionTypeChange(value as SubmissionType)
          }
          w="100%"
        >
          <Stack gap="sm">
            <Radio.Card
              value={SubmissionType.ready}
              withBorder
              radius="md"
              p="md"
              data-testid="ready-claims-card"
            >
              <Group wrap="nowrap" align="center">
                <Radio.Indicator />
                <div>
                  <Text fw={600}>All Ready Claims</Text>
                  <Text size="sm" c="dimmed">
                    Submit every claim currently marked{' '}
                    <Text span fw={600} c="dimmed">
                      Ready.
                    </Text>
                  </Text>
                </div>
              </Group>
            </Radio.Card>

            <Radio.Card
              value={SubmissionType.date}
              withBorder
              radius="md"
              p="md"
              data-testid="date-range-card"
            >
              <Group wrap="nowrap" align="center">
                <Radio.Indicator />
                <div>
                  <Text fw={600}>Service Date Range</Text>
                  <Text size="sm" c="dimmed">
                    Submit claims with service dates in a specific range.
                  </Text>
                </div>
              </Group>
            </Radio.Card>

            <Radio.Card
              value={SubmissionType.provider}
              withBorder
              radius="md"
              p="md"
              data-testid="provider-claims-card"
            >
              <Group wrap="nowrap" align="flex-start">
                <Radio.Indicator />
                <div>
                  <Text fw={600}>Provider Claims</Text>
                  <Text size="sm" c="dimmed">
                    Submit claims for a selected provider.
                  </Text>
                </div>
              </Group>
            </Radio.Card>
          </Stack>
        </Radio.Group>

        {submissionType === SubmissionType.date && (
          <Group justify="center">
            <DatePicker
              value={selectedDateRange}
              onChange={setSelectedDateRange}
              type="range"
              w="55%"
              size="md"
            />
          </Group>
        )}
        {submissionType === SubmissionType.provider && (
          <Stack gap="sm">
            <ProviderSelect
              providers={providers}
              value={selectedProvider || null}
              onChange={(value) => setSelectedProvider(value || null)}
              radius={'md'}
              w={'100%'}
            />
            <Accordion
              variant="separated"
              radius="md"
              defaultValue={hasCompleteDateRange ? 'provider-date-range' : null}
            >
              <Accordion.Item value="provider-date-range">
                <Accordion.Control>
                  <Stack gap={2}>
                    <Text size="sm" fw={600}>
                      Service Date Range (Optional)
                    </Text>
                    <Text size="xs" c="dimmed">
                      Narrow provider claims to a specific service date range.
                    </Text>
                  </Stack>
                </Accordion.Control>
                <Accordion.Panel>
                  <Group justify="center">
                    <DatePicker
                      value={selectedDateRange}
                      onChange={setSelectedDateRange}
                      type="range"
                      w="55%"
                      size="md"
                    />
                  </Group>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Stack>
        )}
        <Stack gap={4} w="100%">
          <Text size="sm" fw={600}>
            Payor
          </Text>
          <Chip.Group
            value={payorType || null}
            multiple={false}
            onChange={(value: string) => {
              setPayorType(value);
            }}
          >
            <Group gap="sm">
              {publicPayors.map((payor) => (
                <Chip
                  key={payor.id}
                  value={payor.name}
                  data-testid={`${payor.name
                    .toLowerCase()
                    .replace(/\s+/g, '-')}-chip`}
                  radius="xl"
                  variant="filled"
                >
                  {payor.name}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        </Stack>
        {validationErrors.length > 0 && (
          <>
            <Divider label="Issues Preventing Submission" />
            <Checkbox
              label="Submit Non-Errored Claims"
              checked={submitNonErrors}
              onChange={(event) =>
                setSubmitNonErrors(event.currentTarget.checked)
              }
            />
            <Alert
              color="red"
              variant="light"
              radius="md"
              title="Submission Blocked"
            >
              {Object.entries(groupedClaimErrors).map(
                ([claimID, messages], index) => {
                  const baseURL = window.location.origin;
                  return (
                    <>
                      <Anchor
                        href={`${baseURL}/claims/edit/${claimID}`}
                        target="_blank"
                        underline="always"
                        rel="noopener noreferrer"
                      >
                        Claim #{index + 1}
                      </Anchor>
                      {messages.length > 0 && (
                        <List>
                          {messages.map((message, idx) => (
                            <List.Item key={`${claimID}-error-${idx}`}>
                              <Text>{message}</Text>
                            </List.Item>
                          ))}
                        </List>
                      )}
                    </>
                  );
                }
              )}
              {generalErrors.map((error, idx) => {
                return (
                  <List>
                    <List.Item key={`${error}-${idx}`}>{error}</List.Item>
                  </List>
                );
              })}
            </Alert>
          </>
        )}
        <Group justify="right" mt="md">
          <Button
            onClick={handleOpenModal}
            color={'grey'}
            c={buttonTextColor(computedColorScheme, false)}
          >
            Cancel
          </Button>
          {validationErrors.length > 0 && !submitNonErrors ? (
            <Button
              color="red"
              onClick={handleReviewErroredClaims}
              disabled={!erroredClaimIDs.length}
              loading={isNavigating}
              c={buttonTextColor(computedColorScheme, false)}
            >
              Review Errored Claims
            </Button>
          ) : (
            <SubmitSplitButton
              color="green"
              c={buttonTextColor(
                computedColorScheme,
                isLoading || disableSubmitButton()
              )}
              handleSubmission={handleSubmission}
              eSubmission={!!apiID}
              disabled={
                (validationErrors.length > 0 && !submitNonErrors) ||
                claimCount === 0 ||
                (claimCount - erroredClaimIDs.length <= 0 && submitNonErrors) ||
                disableSubmitButton()
              }
              buttonText={apiID ? 'Create & Submit' : 'Create'}
              count={
                submitNonErrors
                  ? claimCount - erroredClaimIDs.length
                  : claimCount ?? 0
              }
              isLoading={isLoading}
            />
          )}
        </Group>
      </Stack>
      <ClaimBatchReviewModal
        open={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setIsNavigating(false);
        }}
        claimIds={erroredClaimIDs}
        providers={providers}
        payors={payors}
        table={table}
      />
    </Modal>
  );
};
